// routes.tsx
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import SignIn from "../pages/SignIn/SignIn";
import SignUp from "../pages/SignUp/SignUp";
import EmailVerification from "../pages/EmailVerification/EmailVerification";
import AccountSuccess from "../pages/AccountCreated/AccountCretaed";
import Profile from "../pages/Profile/profile";
import JobSpecification from "../pages/JobSpecification/jobSpecification";
import CondidateSpecification from "../pages/CandidateSpecification/CandidateSpe";
import Error from "../pages/Error/Error";
import ForgotPassword from "../pages/ForgotPassword/ForgotPassword";
import ForgotPasswordEmail from "../pages/ForgotPassword/ForgotPasswordEmail";
import MainLayout from "../layout/MainLayout";
import Dashboard from "../pages/Dashboard/Dashboard";
import JobListing from "../pages/JobSpecification/JobListing";
import JobSummaryListing from "../pages/JobSummary/JobSummaryListing";
import PrivateComponent from "../hooks/AuthComponent";
import ContentCreation from "../pages/ContentCreation/ContentCreation";
import CandidateListing from "../pages/CandidateSpecification/CandidateListing";
import CampaignListing from "../pages/Campaign/Campaign";
import CampaignCreatePage from "../pages/Campaign/CampaignCreatePage";
import CampaignJobPicker from "../pages/Campaign/CampaignJobPicker";
import CampaignEditPage from "../pages/Campaign/CampaignEditPage";
import UserProfile from "../components/Profile/UserProfile";
import UserPassword from "../components/Profile/UserPassword";
import Invitation from "../pages/Invitation/Invitation";
import JobCandiAnalysis from "../pages/JobCandiAnalysis/JobCandiAnalysis";
import GenerateJobOffer from "../pages/GenerateJobOffer/GenerateJobOffer";
import PostingPublishing from "../pages/PostingPublishing/PostingPublishing";
import PostPreview from "../pages/Post Preview/PostPreview";
import SubscriptionPlan from "../pages/SubScriptionPlan/Subscription";
import PaymentGateway from "../pages/PaymentGateway/PaymentGateway";
import PaymentSuccess from "../components/SuccessPayment/SuccessPayment";
import PaymentCancel from "../components/CancelPayment/CancelPayment";
import UpdateContentCreation from "../pages/ContentCreation/UpdateContentCreation";
import TermsCondition from "../components/Privacy-Policy/terms_conditions";
import PrivacyPolicy from "../components/Privacy-Policy/privacy_policy";
import Interviews from "../pages/Interviews/Interviews";
import InterviewProccess from "../pages/Interviews/InterviewProccess";
import ApplicationForm from "../pages/Form/ApplicationForm";
import CandidateLeadsDashboard from "../pages/CandidateLeadsDashboard/Dashboard";
import DashboardView from "../pages/CandidateLeadsDashboard/DashboardView";
import Feedback from "../components/Interview/Feedback";
import CandidateMenu from "../pages/CandidateMenu/CandidateMenu";
import AnalyticsPage from "../pages/Analytics/Analytics";
import { useAuth } from "../hooks/useAuth";
import Loader from "../utils/Loading";
import { Role } from "../constant/RoleConstants";
import JobSummary from "../components/JobSpecification/JobSummary";
import VacancyText from "../pages/VacancyText/VacancyText";
import { SubscriptionProvider } from "../context/SubscriptionContext";
import Subscriptions from "../components/Subscriptions/Subscriptions";
import PaymentSuccessPage from "../pages/Payment/PaymentSuccess";
import PaymentError from "../pages/Payment/PaymentError";
import Billings from "../pages/Billings/Billings";
import UpdateSubscription from "../components/Subscriptions/UpdateSubscription";
import SaveCardInfo from "../pages/Payment/SaveCardInfo";
import JobDescriptionForm from "../pages/JobDescriptionForms/JobDescription";
import EasyApplyForm from "../pages/EasyApplyForm/EasyApplyForm";
// Add helper to check subscription directly for guarding the subscriptions route
import { getCurrentSubscription } from "../helper/subscriptionHelper";
import { useEffect, useState } from "react";

// Guard component: only allow /subscriptions when there is no active plan
const SubscriptionsGuard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [allow, setAllow] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { subscription, isActive } = await getCurrentSubscription();
        if (!isMounted) return;
        const type = String(subscription?.type || "").toLowerCase();
        const isFreePlan = type === "free";
        // Allow subscriptions page when not active OR user is on FREE plan
        setAllow(!isActive || isFreePlan);
      } catch (e) {
        // On error, be safe and allow page to show purchase options
        setAllow(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading) return <Loader />;

  // If user already has an active paid plan (not FREE), render 404 page
  if (!allow) return <Error />;

  // Otherwise show subscriptions page
  return <Subscriptions />;
};

// Guard component: only allow /update-subscriptions for paid plans (BASIC, STANDARD, ADVANCED)
const UpdateSubscriptionsGuard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [allow, setAllow] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { subscription, isActive } = await getCurrentSubscription();
        if (!isMounted) return;
        const type = String(subscription?.type || "").toLowerCase();
        const isPaidPlan = ["basic", "standard", "advanced"].includes(type);
        // Allow update-subscriptions page only for active paid plans
        setAllow(isActive && isPaidPlan);
      } catch (e) {
        // On error, block access
        setAllow(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading) return <Loader />;

  // If user doesn't have an active paid plan, render 404 page
  if (!allow) return <Error />;

  // Otherwise show update subscriptions page
  return <UpdateSubscription />;
};

// Create a wrapper component for routes that need subscription context
const RoutesWithSubscription: React.FC = () => {
  const { user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return "/login";
    if (user.isEmailVerify === false) return "/emailverify";
    if (user.status === Role.HIRING_MANAGER) return "/hiring-manager";
    if (user.status === Role.RECRUITER) return "/";
    return "/login";
  };

  return (
    <Routes>
      <Route
        path="/login"
        element={
          user ? (
            user.isEmailVerify === false ? (
              <Navigate
                to="/emailverify"
                state={{ email: user.email, resendOtp: true }}
                replace
              />
            ) : (
              <Navigate to={getDefaultRoute()} replace />
            )
          ) : (
            <SignIn />
          )
        }
      />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/invitation/:id/:email" element={<Invitation />} />
      <Route path="/forgotpassowrd" element={<ForgotPassword />} />
      <Route path="/forgotpassowrd/email" element={<ForgotPasswordEmail />} />
      <Route path="/emailverify" element={<EmailVerification />} />
      <Route path="/accountsuccess" element={<AccountSuccess />} />
      <Route path="/terms-conditions" element={<TermsCondition />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      {/* <Route path="/submit-application" element={<ApplicationForm />} /> */}
      {/* VONQ */}
      <Route path="/easy-apply/:token" element={<EasyApplyForm />} />
      <Route path="/job-description/:token" element={<JobDescriptionForm />} />
      <Route path="/success" element={<SaveCardInfo />} />
      {/* Subscriptions */}
      <Route path="/subscriptions" element={<SubscriptionsGuard />} />

      {/* RECRUITER routes */}
      <Route
        element={
          <PrivateComponent isAllow={!!user && user.status == Role.RECRUITER} />
        }
      >
        <Route path="/create-company-profile" element={<Profile />} />
        <Route element={<MainLayout />}>
          <Route path="/" element={<Dashboard />} />

          {/* Jobs */}
          <Route path="/jobs" element={<JobListing />} />
          <Route
            path="/jobs/create-jobprofile"
            element={<JobSpecification />}
          />
          <Route path="/jobs/update-job/:id" element={<JobSpecification />} />
          <Route path="/jobs/vacancy-text/:id" element={<VacancyText />} />

          {/* Candidates */}
          <Route path="/candidates" element={<CandidateListing />} />
          <Route
            path="/candidates/create-candidate"
            element={<CondidateSpecification />}
          />
          <Route
            path="/candidates/update-candidate/:id"
            element={<CondidateSpecification />}
          />

          {/* campaigns */}
          <Route path="/campaigns" element={<CampaignListing />} />
          <Route
            path="/campaigns/create/:jobSpecId"
            element={<CampaignCreatePage />}
          />
          <Route
            path="/campaigns/create-campaigns"
            element={<CampaignJobPicker />}
          />
          <Route
            path="/campaigns/edit-campaigns/:id"
            element={<CampaignEditPage />}
          />

          <Route path="/update-company-profile/:id" element={<Profile />} />
          <Route path="/update-user-profile/:id" element={<UserProfile />} />
          <Route path="/update-user-password/:id" element={<UserPassword />} />

          {/* Candidate Menu */}
          <Route path="/candidate-menu" element={<CandidateMenu />} />

          <Route path="/jobcan-analysis" element={<JobCandiAnalysis />} />
          <Route path="/generat-job-offer" element={<GenerateJobOffer />} />
          <Route path="/subscriptionplan" element={<SubscriptionPlan />} />
          <Route path="/payment-gateway/:code" element={<PaymentGateway />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />

          <Route path="/content-creation" element={<ContentCreation />} />
          <Route
            path="/content-update/:id"
            element={<UpdateContentCreation />}
          />
          <Route path="/posting-publishing" element={<PostingPublishing />} />
          <Route path="/post-preview" element={<PostPreview />} />

          {/* Candidate Leads Dashboard  */}
          <Route
            path="/candidate-leads-dashboard"
            element={<CandidateLeadsDashboard />}
          />
          <Route
            path="/candidate-leads-dashboard/view"
            element={<DashboardView />}
          />

          {/* INTERVIEWS  */}
          <Route path="/interview-proccess" element={<InterviewProccess />} />

          {/* giveFeedback */}
          <Route path="/feedback" element={<Feedback />} />

          {/* Analytics */}
          <Route path="/analytics" element={<AnalyticsPage />} />

          
          <Route
            path="/update-subscriptions"
            element={<UpdateSubscriptionsGuard />}
          />

          {/* Payment */}
          <Route path="/payment-done" element={<PaymentSuccessPage />} />
          <Route path="/payment-error" element={<PaymentError />} />

          {/* Billings */}
          <Route path="/billings" element={<Billings />} />
        </Route>
      </Route>

      {/* Hiring manager routes */}
      <Route
        element={
          <PrivateComponent
            isAllow={!!user && user.status == Role.HIRING_MANAGER}
          />
        }
      >
        <Route path="/hiring-manager" element={<MainLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="generat-job-offer" element={<GenerateJobOffer />} />
          <Route path="analytics" element={<AnalyticsPage />} />

          {/* Jobs */}
          <Route path="jobs" element={<JobListing />} />
          <Route path="jobs/create-jobprofile" element={<JobSpecification />} />
          <Route path="jobs/update-job/:id" element={<JobSpecification />} />

          {/* Job-summary */}
          <Route path="job-summary" element={<JobSummaryListing />} />
          <Route path="job-summary/:id" element={<JobSummary />} />
          <Route path="jobs/vacancy-text/:id" element={<VacancyText />} />

          {/* Candidates */}
          <Route path="candidates" element={<CandidateListing />} />
          <Route
            path="candidates/create-candidate"
            element={<CondidateSpecification />}
          />
          <Route
            path="candidates/update-candidate/:id"
            element={<CondidateSpecification />}
          />

          <Route path="update-user-profile/:id" element={<UserProfile />} />
          <Route path="update-user-password/:id" element={<UserPassword />} />

          <Route path="candidate-menu" element={<CandidateMenu />} />
          <Route
            path="candidate-leads-dashboard"
            element={<CandidateLeadsDashboard />}
          />
          <Route
            path="candidate-leads-dashboard/view"
            element={<DashboardView />}
          />
          <Route path="feedback" element={<Feedback />} />

          {/* Subscriptions */}
          <Route path="/hiring-manager/subscriptions" element={<SubscriptionsGuard />} />
          <Route path="/hiring-manager/update-subscriptions" element={<UpdateSubscriptionsGuard />} />

          {/* Payment */}
          <Route path="payment-done" element={<PaymentSuccessPage />} />
          <Route path="payment-error" element={<PaymentError />} />
        </Route>
      </Route>

      <Route path="/*" element={<Error />} />
    </Routes>
  );
};

const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();

  return isLoading ? (
    <Loader />
  ) : (
    <Router>
      <SubscriptionProvider>
        <RoutesWithSubscription />
      </SubscriptionProvider>
    </Router>
  );
};

export default AppRoutes;
